/** Attributes that carry player-facing text in our templates. */
const TEXT_ATTRIBUTES = ['title', 'aria-label', 'placeholder', 'alt', 'data-pad-back-label', 'data-pad-verb'] as const;

/**
 * Re-translates a live, already-mounted template in place, for language
 * switches that must not tear down the DOM (the race HUD keeps its canvas,
 * listeners and live values). `before` and `after` are the same template
 * rendered in the old and new language. A text node or attribute only
 * changes while it still holds the old template's static value, so anything
 * the game has since written (lap counters, positions) is left for its own
 * next update. Nodes the app inserted at runtime are skipped by alignment.
 */
export function relocaliseTree(live: Element, before: string, after: string): void {
  const parse = (html: string): DocumentFragment => {
    const template = document.createElement('template');
    template.innerHTML = html;
    return template.content;
  };
  walkChildren(live, parse(before), parse(after));
}

function walkChildren(live: Node, before: Node, after: Node): void {
  const liveChildren = [...live.childNodes];
  const beforeChildren = [...before.childNodes];
  const afterChildren = [...after.childNodes];
  if (beforeChildren.length !== afterChildren.length) {
    // A translation changed the markup shape (e.g. an extra <br>): swap the
    // whole static block if the app has not touched it.
    if (live instanceof Element && before instanceof Element && after instanceof Element && live.innerHTML === before.innerHTML) {
      live.innerHTML = after.innerHTML;
    }
    return;
  }
  let cursor = 0;
  for (let index = 0; index < beforeChildren.length; index += 1) {
    const old = beforeChildren[index]!;
    const match = findMatch(liveChildren, cursor, old);
    if (match < 0) continue;
    cursor = match + 1;
    relocaliseNode(liveChildren[match]!, old, afterChildren[index]!);
  }
}

function findMatch(candidates: readonly Node[], from: number, template: Node): number {
  for (let index = from; index < candidates.length; index += 1) {
    if (sameShape(candidates[index]!, template)) return index;
  }
  return -1;
}

function sameShape(live: Node, template: Node): boolean {
  if (live.nodeType !== template.nodeType) return false;
  if (!(live instanceof Element) || !(template instanceof Element)) return true;
  return live.tagName === template.tagName && live.id === template.id;
}

function relocaliseNode(live: Node, before: Node, after: Node): void {
  if (live.nodeType === Node.TEXT_NODE) {
    if (live.nodeValue === before.nodeValue && before.nodeValue !== after.nodeValue) live.nodeValue = after.nodeValue;
    return;
  }
  if (!(live instanceof Element) || !(before instanceof Element) || !(after instanceof Element)) return;
  if (live.tagName !== after.tagName) return;
  for (const name of TEXT_ATTRIBUTES) {
    const old = before.getAttribute(name);
    const next = after.getAttribute(name);
    if (old === null || next === null || old === next) continue;
    if (live.getAttribute(name) === old) live.setAttribute(name, next);
  }
  walkChildren(live, before, after);
}
